// Nuxt auto-imports useRuntimeConfig; no import needed.

interface OrderCompleteInfo {
  orderId: string
  raw: unknown
}

interface ModalCheckoutParams {
  eventId: string
  promoCode: string
  onOrderComplete: (info: OrderCompleteInfo) => void
  onModalClose?: () => void
}

interface EmbeddedCheckoutParams {
  eventId: string
  promoCode: string
  containerId: string
  containerHeight?: number
  onOrderComplete: (info: OrderCompleteInfo) => void
}

// Do not redeclare window.EBWidgets types here; rely on existing project typings or use runtime checks.

let scriptLoaded = false
let scriptLoadingPromise: Promise<void> | null = null

type EBWidgetsApi = {
  createWidget: (options: unknown) => unknown
}

function getEBWidgets(): EBWidgetsApi | undefined {
  if (typeof window === 'undefined') return undefined
  const w = window as unknown as { EBWidgets?: EBWidgetsApi }
  return w.EBWidgets
}

export async function ensureEventbriteScriptLoaded(): Promise<void> {
  if (typeof window === 'undefined') return
  if (getEBWidgets()) {
    scriptLoaded = true
    return
  }
  if (scriptLoaded) return
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const config = useRuntimeConfig()
    // Use krewebacchusEnv for reliable environment detection
    const env = (config.public?.krewebacchusEnv || 'dev').toString().toLowerCase()
    const isQAMode = env !== 'production' && env !== 'prod'
    const scriptSrc = isQAMode
      ? 'https://www.evbqa.com/static/widgets/eb_widgets.js'
      : 'https://www.eventbrite.com/static/widgets/eb_widgets.js'

    const script = document.createElement('script')
    script.src = scriptSrc
    script.async = true

    script.onload = () => {
      // Small delay to ensure EBWidgets is ready
      setTimeout(() => {
        if (getEBWidgets()) {
          scriptLoaded = true
          resolve()
        }
        else {
          reject(new Error('EBWidgets not available after script load'))
        }
      }, 200)
    }

    script.onerror = () => reject(new Error('Failed to load Eventbrite widget script'))

    document.head.appendChild(script)
  })

  return scriptLoadingPromise
}

function normalizeOrderComplete(input: unknown): OrderCompleteInfo {
  if (input && typeof input === 'object' && 'orderId' in (input as Record<string, unknown>)) {
    const obj = input as { orderId?: unknown }
    return { orderId: String(obj.orderId ?? ''), raw: input }
  }
  if (typeof input === 'string') {
    const match = input.match(/order[_-]?([A-Za-z0-9-]+)/i)
    return { orderId: match?.[1] ?? input, raw: input }
  }
  return { orderId: '', raw: input }
}

export async function openEventbriteModalCheckout(params: ModalCheckoutParams): Promise<void> {
  await ensureEventbriteScriptLoaded()
  const api = getEBWidgets()
  if (!api) throw new Error('Eventbrite widget not available')

  // Create a hidden trigger element for the EB modal
  const triggerId = `eb-modal-trigger-${Math.random().toString(36).slice(2)}`
  const el = document.createElement('button')
  el.id = triggerId
  el.type = 'button'
  el.style.position = 'fixed'
  el.style.left = '-9999px'
  el.style.top = '-9999px'
  el.setAttribute('aria-hidden', 'true')
  document.body.appendChild(el)

  try {
    const options: Record<string, unknown> = {
      widgetType: 'checkout',
      eventId: params.eventId,
      promoCode: params.promoCode,
      // Modal-specific options (suppressed from strict typing via Record)
      modal: true,
      modalTriggerElementId: triggerId,
      onOrderComplete: (payload: unknown) => {
        const info = normalizeOrderComplete(payload)
        params.onOrderComplete(info)
      },
      onModalClose: () => {
        params.onModalClose?.()
      },
    }
    api.createWidget(options)

    // Open the modal shortly after widget is created
    setTimeout(() => el.click(), 50)
  }
  finally {
    // Clean up trigger after a while to avoid DOM clutter
    setTimeout(() => {
      try {
        document.body.removeChild(el)
      }
      catch { /* noop */ }
    }, 5000)
  }
}

export async function createEmbeddedCheckout(params: EmbeddedCheckoutParams): Promise<void> {
  await ensureEventbriteScriptLoaded()
  const api = getEBWidgets()
  if (!api) throw new Error('Eventbrite widget not available')

  const options: Record<string, unknown> = {
    widgetType: 'checkout',
    eventId: params.eventId,
    promoCode: params.promoCode,
    iframeContainerId: params.containerId,
    iframeContainerHeight: params.containerHeight ?? 600,
    onOrderComplete: (payload: unknown) => {
      const info = normalizeOrderComplete(payload)
      params.onOrderComplete(info)
    },
  }
  api.createWidget(options)
}
